import { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Loading,
} from "../../../../components";
import "../../configuracoes.style.css";
import {
  notification,
  AdzapService,
  SessionService,
} from "../../../../services";
import { responseErros } from "../../../../utils";
import store from "../../../../config/store";
import { io } from "socket.io-client";

const IntegracoesTab = () => {
  const adzapService = new AdzapService();
  const sessionService = new SessionService();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("disconnected");
  const [qrCode, setQrCode] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const intervaloPollingRef = useRef(null);
  const socketRef = useRef(null);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);

  const images = {
    bg: "https://public.siafevolution.com.br/bg-whatsapp.png",
    icon: "https://public.siafevolution.com.br/webzap.png",
  };

  const cleanup = useCallback(() => {
    if (intervaloPollingRef.current) {
      clearInterval(intervaloPollingRef.current);
      intervaloPollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const validarConexao = useCallback(async () => {
    try {
      const result = await adzapService.statusInstancia();
      if (!result.isAxiosError && result.data) {
        if (mountedRef.current) {
          verificaStatus({
            status: result.data,
            qrcode: null,
          });
        }
        return result.data;
      } else {
        if (mountedRef.current) {
          handleDisconnected();
        }
        return "disconnected";
      }
    } catch (error) {
      if (mountedRef.current) {
        handleDisconnected();
      }
      return "disconnected";
    }
  }, []);

  const startPolling = useCallback(() => {
    if (status === "connected" || isPolling) {
      return;
    }
    cleanup();
    setIsPolling(true);

    intervaloPollingRef.current = setInterval(async () => {
      if (!mountedRef.current) return;
      const currentStatus = await validarConexao();
      if (currentStatus === "connected") {
        cleanup();
      }
    }, 5000);

    timeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      cleanup();
      if (status === "connecting") {
        handleDisconnected();
        notification.erro("QR Code expirado. Gere um novo QR Code.");
      }
    }, 300000);
  }, [status, isPolling, validarConexao, cleanup]);

  const stopPolling = useCallback(() => {
    cleanup();
  }, [cleanup]);

  useEffect(() => {
    mountedRef.current = true;
    const usuario = sessionService.getEmpresa();
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const newSocket = io(`${store.baseUrlApiAdzap}`, {
      path: "/socket",
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      console.log("Conectado ao servidor Socket");
    });

    newSocket.on(`connection_update:${usuario.cnpjCpf}`, (data) => {
      if (!mountedRef.current) return;
      verificaStatus(data);
      if (data.status === "connected" || data.status === "disconnected") {
        stopPolling();
      }
    });
    return () => {
      mountedRef.current = false;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      cleanup();
    };
  }, []);

  useEffect(() => {
    validarConexao();
  }, [validarConexao]);

  async function iniciarInstancia() {
    if (isConnecting || loading) {
      return;
    }

    setLoading(true);
    setIsConnecting(true);

    try {
      const result = await adzapService.iniciarInstancia();

      if (!mountedRef.current) return;

      if (!result.isAxiosError && result.data) {
        setQrCode(result.data);
        setStatus("connecting");
        startPolling();
      } else {
        responseErros(result);
        handleDisconnected();
      }
    } catch (error) {
      console.error("Erro ao iniciar instância:", error);
      if (mountedRef.current) {
        notification.erro("Erro ao gerar QR Code");
        handleDisconnected();
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setIsConnecting(false);
      }
    }
  }

  function handleDisconnected() {
    setQrCode(null);
    setStatus("disconnected");
    setIsConnecting(false);
    stopPolling();
  }

  async function verificaStatus(instance) {
    const { status: statusInstancia, qrcode: qrcodeUpdated } = instance;
    if (!mountedRef.current) return;
    if (status === statusInstancia && !qrcodeUpdated) {
      return;
    }

    setStatus(statusInstancia);

    if (statusInstancia === "connecting") {
      if (qrcodeUpdated) {
        setQrCode(qrcodeUpdated);
      }
      if (!isPolling) {
        startPolling();
      }
    } else if (statusInstancia === "connected") {
      notification.sucessoGenericos("WhatsApp conectado com sucesso!");
      setQrCode(null);
      setIsConnecting(false);
      stopPolling();
    } else if (statusInstancia === "disconnected") {
      handleDisconnected();
    }
  }

  return (
    <Card className="p-2">
      <CardContent
        style={{ background: `url(${images.bg})` }}
        className="d-flex px-5 py-5"
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} className="d-flex">
            <div className="me-4">
              <div className="d-flex justify-content-center align-items-center">
                <img
                  style={{ width: "65%" }}
                  src={images.icon}
                  alt="Whatsapp Icon"
                />
              </div>
              <div className="mt-4">
                {status === "connected" ? (
                  <>
                    <h4
                      className="fw-bolder text-uppercase"
                      style={{ color: "#25d366" }}
                    >
                      WhatsApp conectado!
                    </h4>
                    <p className="text-muted">
                      A integração com o sistema foi realizada com sucesso.
                    </p>
                  </>
                ) : status === "connecting" ? (
                  <>
                    <h4
                      className="fw-bolder text-uppercase"
                      style={{ color: "#AFE966" }}
                    >
                      Aguardando Conexão...
                    </h4>
                    <p className="text-muted">
                      Escaneie o QR Code com seu WhatsApp para conectar.
                    </p>
                    {qrCode && (
                      <>
                        <p className="text-muted small">
                          O QR Code expira em 5 minutos.
                        </p>
                        {isPolling && (
                          <p className="text-muted small">
                            <i className="ph-spin ph-spinner me-1"></i>
                            Verificando conexão...
                          </p>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <h4
                      className="fw-bolder text-uppercase"
                      style={{ color: "#25d366" }}
                    >
                      Nenhum número conectado
                    </h4>
                    <p className="text-muted">
                      Para iniciar a integração, adicione um número de telefone
                      ao sistema
                    </p>
                    <Button
                      variant="contained"
                      fullWidth
                      className="mt-3"
                      sx={{
                        boxShadow: "none",
                        background: "#25d366",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#19A24C",
                        },
                      }}
                      onClick={iniciarInstancia}
                      disabled={loading || isConnecting}
                    >
                      {loading || isConnecting ? "GERANDO..." : "GERAR QR CODE"}
                    </Button>
                  </>
                )}
              </div>
            </div>
            <hr className="vr" />
          </Grid>
          <Grid item xs={12} md={8} className={"d-flex align-items-center"}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <List>
                  <ListItem>
                    <ListItemText primary="1. Abra o WhatsApp no celular;" />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <>
                          {`2. Clique em "Mais opções"`}{" "}
                          <i
                            className="ph-bold ph-dots-three-vertical"
                            style={{ fontSize: "1.1rem" }}
                          ></i>{" "}
                          {`no Android, ou em
                        "Configurações"`}{" "}
                          <i
                            className="ph-fill ph-gear-six"
                            style={{ fontSize: "1.1rem" }}
                          ></i>{" "}
                          no iPhone;
                        </>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="3. Toque em 'Dispositivos conectados' e, em seguida, em 'Conectar dispositivo';" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="4. Aponte o celular para escanear o QR Code." />
                  </ListItem>
                </List>
              </Grid>
              <Grid
                item
                xs={12}
                md={5}
                className="d-flex flex-column justify-content-center align-items-center text-center"
              >
                {qrCode ? (
                  <div>
                    <img
                      src={qrCode}
                      alt="QR Code"
                      style={{
                        width: "225px",
                        height: "225px",
                        objectFit: "contain",
                        border: "2px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "8px",
                        backgroundColor: "white",
                      }}
                    />
                    {isPolling && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="ph-spin ph-spinner me-1"></i>
                          Aguardando conexão...
                        </small>
                      </div>
                    )}
                  </div>
                ) : status === "connecting" ? (
                  <div className="text-center">
                    <div
                      className="spinner-border text-success mb-3"
                      role="status"
                    >
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="text-muted">Gerando QR Code...</p>
                  </div>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
      <Loading loading={loading} />
    </Card>
  );
};

export default IntegracoesTab;
